import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { apiService, ApiError } from '../../services/api';
import { countries, getDefaultCountry, validatePhoneNumber, formatPhoneNumber as formatPhone } from '../../utils/phoneValidation';
import { parseExpiresIn } from '../../utils/timeParser';
import './SignIn.css';

type SignInFormState = {
  phone: string;
  identifier: string;
  otpSent: boolean;
  countryCode: string;
  message: string;
  expiresIn: number;
  otp: string;
};

const initialState: SignInFormState = {
  phone: '',
  identifier: '',
  otpSent: false,
  countryCode: getDefaultCountry().code,
  message: '',
  expiresIn: 0,
  otp: '',
};

const SignIn: React.FC = () => {
  const [formValues, setFormValues] = useState<SignInFormState>(initialState);
  const [statusMessage, setStatusMessage] = useState<string>('');
  const [countdown, setCountdown] = useState<number>(0);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const countdownTimerRef = useRef<NodeJS.Timeout | null>(null);
  const { login } = useAuth();
  const navigate = useNavigate();

  // Cleanup countdown timer on unmount
  useEffect(() => {
    return () => {
      if (countdownTimerRef.current) {
        clearInterval(countdownTimerRef.current);
      }
    };
  }, []);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = event.target;
    const field = name as keyof SignInFormState;
    
    if (field === 'countryCode') {
      setFormValues((previousValues) => ({
        ...previousValues,
        [field]: value,
        phone: '', // Reset phone when country changes
      }));
    } else if (field === 'phone') {
      const country = countries.find(c => c.code === formValues.countryCode) || getDefaultCountry();
      const digitsOnly = value.replace(/\D/g, '');
      const maxLength = country.phoneLength;
      const formatted = digitsOnly.slice(0, maxLength);
      setFormValues((previousValues) => ({
        ...previousValues,
        [field]: formatted,
      }));
    } else {
      // OTP input - only numbers, max 6 digits
      const formatted = value.replace(/\D/g, '').slice(0, 6);
      setFormValues((previousValues) => ({
        ...previousValues,
        [field]: formatted,
      }));
    }
    setStatusMessage('');
  };

  const startCountdown = (seconds: number = 60) => {
    setCountdown(seconds);
    
    // Clear existing timer
    if (countdownTimerRef.current) {
      clearInterval(countdownTimerRef.current);
    }
    
    // Start new countdown
    countdownTimerRef.current = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          if (countdownTimerRef.current) {
            clearInterval(countdownTimerRef.current);
            countdownTimerRef.current = null;
          }
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const handleSendOTP = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!validatePhoneNumber(formValues.phone, formValues.countryCode)) {
      const country = countries.find(c => c.code === formValues.countryCode) || getDefaultCountry();
      setStatusMessage(`Please enter a valid ${country.phoneLength}-digit phone number.`);
      return;
    }

    setIsLoading(true);
    setStatusMessage('Sending OTP...');

    try {
      const fullPhone = `${formValues.countryCode}${formValues.phone}`;
      const response = await apiService.sendOTP(fullPhone);
      
      if (response.otpSent) {
        const expiresInSeconds = parseExpiresIn(response.expiresIn);
        setFormValues((prev) => ({ 
          ...prev, 
          otpSent: true,
          identifier: response.identifier,
          message: response.message,
          expiresIn: expiresInSeconds
        }));
        setStatusMessage(response.message || 'OTP sent to your phone!');
        startCountdown(expiresInSeconds);
      } else {
        setStatusMessage(response.message || 'Failed to send OTP. Please try again.');
      }
    } catch (error) {
      if (error instanceof ApiError) {
        setStatusMessage(error.message || 'Failed to send OTP. Please try again.');
      } else {
        setStatusMessage('An unexpected error occurred. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendOTP = async () => {
    if (countdown > 0 || isLoading) return;
    
    setIsLoading(true);
    setStatusMessage('Resending OTP...');

    try {
      const fullPhone = formValues.identifier || `${formValues.countryCode}${formValues.phone}`;
      const response = await apiService.resendOTP(fullPhone);
      
      if (response.otpSent) {
        const expiresInSeconds = parseExpiresIn(response.expiresIn);
        setFormValues((prev) => ({ 
          ...prev,
          identifier: response.identifier,
          message: response.message,
          expiresIn: expiresInSeconds
        }));
        setStatusMessage(response.message || 'OTP resent to your phone!');
        startCountdown(expiresInSeconds);
      } else {
        setStatusMessage(response.message || 'Failed to resend OTP. Please try again.');
      }
    } catch (error) {
      if (error instanceof ApiError) {
        setStatusMessage(error.message || 'Failed to resend OTP. Please try again.');
      } else {
        setStatusMessage('An unexpected error occurred. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifyOTP = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!formValues.otp.trim() || formValues.otp.length !== 6) {
      setStatusMessage('Please enter the 6-digit OTP.');
      return;
    }

    setIsLoading(true);
    setStatusMessage('Verifying OTP...');

    try {
      const identifier = formValues.identifier || `${formValues.countryCode}${formValues.phone}`;
      const response = await apiService.verifyOTP(identifier, formValues.otp);
      
      // Store token if provided
      if (response.token) {
        localStorage.setItem('authToken', response.token);
      }
      
      // Update auth context
      login();
      setStatusMessage(response.message || 'Successfully signed in!');
      
      // Clear countdown timer
      if (countdownTimerRef.current) {
        clearInterval(countdownTimerRef.current);
        countdownTimerRef.current = null;
      }
      
      // Reset form
      setFormValues(initialState);
      
      // Redirect to dashboard after a brief delay
      setTimeout(() => {
        navigate('/dashboard');
      }, 500);
    } catch (error) {
      if (error instanceof ApiError) {
        if (error.status === 401 || error.status === 403) {
          setStatusMessage('Invalid or expired OTP. Please request a new one.');
          setFormValues((prev) => ({ ...prev, otp: '', otpSent: false }));
        } else {
          setStatusMessage(error.message || 'Failed to verify OTP. Please try again.');
        }
      } else {
        setStatusMessage('An unexpected error occurred. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const currentCountry = countries.find(c => c.code === formValues.countryCode) || getDefaultCountry();
  const displayPhone = formatPhone(formValues.phone, formValues.countryCode);

  return (
    <div className="signin-card">
      <header className="signin-header">
        <h1>Welcome back</h1>
        <p>Sign in with your phone number to access your dashboard.</p>
      </header>

      {!formValues.otpSent ? (
        <form className="signin-form" onSubmit={handleSendOTP} noValidate>
          <label className="signin-label" htmlFor="phone">
            Phone Number
          </label>
          <div className="signin-phone-input-wrapper">
            <select
              name="countryCode"
              value={formValues.countryCode}
              onChange={handleChange}
              className="signin-country-select"
            >
              {countries.map((country) => (
                <option key={country.code} value={country.code}>
                  {country.code}
                </option>
              ))}
            </select>
            <input
              id="phone"
              name="phone"
              type="tel"
              autoComplete="tel"
              value={displayPhone}
              onChange={handleChange}
              placeholder={`Enter ${currentCountry.phoneLength} digits`}
              maxLength={currentCountry.phoneLength + 5} // Account for formatting characters
              required
              className="signin-phone-input"
            />
          </div>

          <button 
            className="signin-submit" 
            type="submit"
            disabled={isLoading}
          >
            {isLoading ? 'Sending...' : 'Send OTP'}
          </button>

          {statusMessage && <p className="signin-status">{statusMessage}</p>}
        </form>
      ) : (
        <form className="signin-form" onSubmit={handleVerifyOTP} noValidate>
          <label className="signin-label" htmlFor="otp">
            Enter OTP
          </label>
          <div className="signin-otp-info">
            <p className="signin-otp-text">
              We sent a 6-digit code to <strong>{formValues.identifier || `${formValues.countryCode} ${displayPhone}`}</strong>
            </p>
          </div>
          <input
            id="otp"
            name="otp"
            type="text"
            inputMode="numeric"
            autoComplete="one-time-code"
            value={formValues.otp}
            onChange={handleChange}
            placeholder="000000"
            maxLength={6}
            required
            className="signin-otp-input"
          />

          <div className="signin-otp-actions">
            <button
              type="button"
              className="signin-link"
              onClick={handleResendOTP}
              disabled={countdown > 0 || isLoading}
            >
              {countdown > 0 ? `Resend OTP in ${countdown}s` : isLoading ? 'Resending...' : 'Resend OTP'}
            </button>
            <button
              type="button"
              className="signin-link"
              onClick={() => setFormValues(initialState)}
            >
              Change Phone
            </button>
          </div>

          <button 
            className="signin-submit" 
            type="submit"
            disabled={isLoading}
          >
            {isLoading ? 'Verifying...' : 'Verify OTP'}
          </button>

          {statusMessage && <p className="signin-status">{statusMessage}</p>}
        </form>
      )}
    </div>
  );
};

export default SignIn;

